/**
 * ObradorsList (P-01 — list) — the tenant directory.
 *
 * Columns: nom comercial · NIF · sector · pla · estat · data d'alta · últim accés.
 * Filters: estat + sector + pla. Search by nom or NIF. Deactivated tenants are
 * HIDDEN by default behind a "mostrar inactius" toggle.
 *
 * Reads via `useTenants()` (insforge.database.from('tenants')); filtering and
 * search are client-side over the small directory.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Button, Card, Field, useTheme } from '@obrava/ui';
import type {
  TenantStatus,
  TenantSector,
  TenantPlan,
} from '@obrava/shared';
import { PageHeading } from '../../components/layout.js';
import { Banner } from '../../components/Banner.js';
import { Badge } from '../../components/Badge.js';
import { Select } from '../../components/Select.js';
import { useLanguage } from '../../providers/I18nProvider.js';
import { formatDate } from '../../lib/format.js';
import {
  sectorLabel,
  planLabel,
  statusLabel,
  statusOptions,
  sectorOptions,
  planOptions,
} from './labels.js';
import { useTenants, type TenantRow } from './data.js';

type StatusFilter = TenantStatus | 'all';
type SectorFilter = TenantSector | 'all';
type PlanFilter = TenantPlan | 'all';

function matches(
  row: TenantRow,
  q: string,
  status: StatusFilter,
  sector: SectorFilter,
  plan: PlanFilter,
  showInactive: boolean,
): boolean {
  // Hide deactivated tenants unless explicitly shown.
  if (!showInactive && row.status === 'deactivated') return false;
  if (status !== 'all' && row.status !== status) return false;
  if (sector !== 'all' && row.sector !== sector) return false;
  if (plan !== 'all' && row.plan !== plan) return false;
  if (q) {
    const needle = q.trim().toLowerCase();
    const inName = row.name.toLowerCase().includes(needle);
    const inTaxId = (row.tax_id ?? '').toLowerCase().includes(needle);
    if (!inName && !inTaxId) return false;
  }
  return true;
}

export function ObradorsList(): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { language } = useLanguage();
  // `isPending` (not `isLoading`): while the query is gated off — signed out, or
  // the session still resolving — it stays pending, so the screen shows the loader
  // instead of an "empty directory" that reads like real data.
  const { data, isPending, isError } = useTenants();

  const [q, setQ] = React.useState('');
  const [status, setStatus] = React.useState<StatusFilter>('all');
  const [sector, setSector] = React.useState<SectorFilter>('all');
  const [plan, setPlan] = React.useState<PlanFilter>('all');
  const [showInactive, setShowInactive] = React.useState(false);

  const rows = data ?? [];
  const filtered = rows.filter((r) =>
    matches(r, q, status, sector, plan, showInactive),
  );

  const allOption = { value: 'all', label: t('common:all') };

  const cellHeaderStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: `${theme.spacing.space[3]}px ${theme.spacing.space[4]}px`,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.caption,
    fontWeight: theme.typography.weight.semibold,
    color: theme.color.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: theme.typography.letterSpacing.wide,
    borderBottom: `1px solid ${theme.color.divider}`,
    whiteSpace: 'nowrap',
  };

  const cellStyle: React.CSSProperties = {
    padding: `${theme.spacing.space[4]}px`,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.body,
    color: theme.color.textPrimary,
    borderBottom: `1px solid ${theme.color.divider}`,
    whiteSpace: 'nowrap',
  };

  return (
    <div>
      <PageHeading
        title={t('platformConsole.obradors:title')}
        subtitle={t('platformConsole.obradors:subtitle')}
        action={
          <Button onClick={() => void navigate({ to: '/tenants/new' })}>
            {t('platformConsole.obradors:create')}
          </Button>
        }
      />

      <Card style={{ marginBottom: theme.spacing.space[6] }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: theme.spacing.space[4],
            alignItems: 'end',
          }}
        >
          <Field
            label={t('platformConsole.obradors:searchPlaceholder')}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select
            label={t('platformConsole.obradors:filterStatus')}
            value={status}
            options={[allOption, ...statusOptions(t)]}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
          />
          <Select
            label={t('platformConsole.obradors:filterSector')}
            value={sector}
            options={[allOption, ...sectorOptions(t)]}
            onChange={(e) => setSector(e.target.value as SectorFilter)}
          />
          <Select
            label={t('platformConsole.obradors:filterPlan')}
            value={plan}
            options={[allOption, ...planOptions(t)]}
            onChange={(e) => setPlan(e.target.value as PlanFilter)}
          />
        </div>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing.space[3],
            marginTop: theme.spacing.space[5],
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.footnote,
            color: theme.color.textSecondary,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {t('platformConsole.obradors:showInactive')}
        </label>
      </Card>

      {isError ? (
        <Banner tone="danger">{t('platformConsole.common:loadError')}</Banner>
      ) : isPending ? (
        <p
          style={{
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.body,
            color: theme.color.textSecondary,
          }}
        >
          {t('common:loading')}
        </p>
      ) : filtered.length === 0 ? (
        <Card>
          <p
            style={{
              margin: 0,
              fontFamily: theme.typography.family.sans,
              fontSize: theme.typography.size.body,
              color: theme.color.textSecondary,
            }}
          >
            {rows.length === 0
              ? t('platformConsole.obradors:empty')
              : t('platformConsole.obradors:emptyFiltered')}
          </p>
        </Card>
      ) : (
        <Card padding={0} style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={cellHeaderStyle}>{t('platformConsole.obradors:colName')}</th>
                  <th style={cellHeaderStyle}>{t('platformConsole.obradors:colTaxId')}</th>
                  <th style={cellHeaderStyle}>{t('platformConsole.obradors:colSector')}</th>
                  <th style={cellHeaderStyle}>{t('platformConsole.obradors:colPlan')}</th>
                  <th style={cellHeaderStyle}>{t('platformConsole.obradors:colStatus')}</th>
                  <th style={cellHeaderStyle}>{t('platformConsole.obradors:colCreatedAt')}</th>
                  <th style={cellHeaderStyle}>{t('platformConsole.obradors:colLastAccess')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => void navigate({ to: '/tenants/$id', params: { id: row.id } })}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ ...cellStyle, fontWeight: theme.typography.weight.medium }}>
                      {row.name}
                    </td>
                    <td style={{ ...cellStyle, color: theme.color.textSecondary }}>
                      {row.tax_id ?? '—'}
                    </td>
                    <td style={cellStyle}>{sectorLabel(t, row.sector)}</td>
                    <td style={cellStyle}>{planLabel(t, row.plan)}</td>
                    <td style={cellStyle}>
                      <Badge status={row.status} label={statusLabel(t, row.status)} />
                    </td>
                    <td style={{ ...cellStyle, color: theme.color.textSecondary }}>
                      {formatDate(row.created_at, language) ?? '—'}
                    </td>
                    <td style={{ ...cellStyle, color: theme.color.textSecondary }}>
                      {formatDate(row.last_access_at, language) ??
                        t('platformConsole.common:never')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
